/**
 * EngineSelector Component
 *
 * Radio group for Persona Engine engine selection using bordered motion cards.
 * Each option displays the engine name, plain-language description,
 * and default confidence threshold. "Auto" indicates which engine
 * would be auto-selected for the current product lens.
 */

import { useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cpu, Check, Sparkles } from 'lucide-react'
import type { PersonaEngineInfo, EngineType } from '@personic/shared/types/persona-engine'
import { ENGINE_LABELS, ENGINE_SHORT_LABELS } from '@personic/shared/types/persona-engine'

// ─── Engine descriptions (plain language) ────────────────────────────────────

const ENGINE_DESCRIPTIONS: Record<string, string> = {
  auto: 'Automatically selects the best engine based on product context and available signals',
  ipc: 'Maps dominance and warmth axes for complementary persona positioning',
  cat: 'Converges communication style toward the profile source with damping',
  rft: 'Classifies promotion vs prevention focus for messaging strategy',
  aic: 'Infers interaction style to calibrate warmth and consistency',
  mi: 'Builds OARS profile for behavior change contexts',
  scarf: 'Scores threat/reward domains to maximize psychological safety',
}

const DEFAULT_THRESHOLDS: Record<string, number> = {
  cat: 0.35,
  ipc: 0.45,
  mi: 0.45,
  rft: 0.50,
  scarf: 0.50,
  aic: 0.55,
}

// ─── Auto-select heuristic (maps product lens to recommended engine) ─────────

const PRODUCT_LENS_ENGINE_MAP: Record<string, EngineType> = {
  sales: 'ipc',
  support: 'cat',
  coaching: 'mi',
  marketing: 'rft',
  hr: 'scarf',
  default: 'aic',
}

function getAutoSelectedEngine(productLens?: string): EngineType {
  if (!productLens) return 'aic'
  return PRODUCT_LENS_ENGINE_MAP[productLens.toLowerCase()] ?? 'aic'
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface EngineSelectorProps {
  value: string
  onChange: (engine: string) => void
  productLens?: string
  engines?: PersonaEngineInfo[]
  disabled?: boolean
}

interface EngineOption {
  key: string
  label: string
  shortLabel: string
  description: string
  threshold: number | null
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function EngineSelector({
  value,
  onChange,
  productLens,
  engines,
  disabled = false,
}: EngineSelectorProps) {
  const autoEngine = getAutoSelectedEngine(productLens)

  const options: EngineOption[] = useMemo(() => {
    const autoOption: EngineOption = {
      key: 'auto',
      label: 'Auto',
      shortLabel: 'AUTO',
      description: ENGINE_DESCRIPTIONS.auto,
      threshold: null,
    }

    const engineOptions: EngineOption[] = (
      engines
        ? engines.map((e) => ({
            key: e.engine_name,
            label: ENGINE_LABELS[e.engine_name] || e.engine_name.toUpperCase(),
            shortLabel: ENGINE_SHORT_LABELS[e.engine_name] || e.engine_name.toUpperCase(),
            description: ENGINE_DESCRIPTIONS[e.engine_name] || e.description,
            threshold: e.default_threshold,
          }))
        : (['ipc', 'cat', 'rft', 'aic', 'mi', 'scarf'] as EngineType[]).map((key) => ({
            key,
            label: ENGINE_LABELS[key],
            shortLabel: ENGINE_SHORT_LABELS[key],
            description: ENGINE_DESCRIPTIONS[key],
            threshold: DEFAULT_THRESHOLDS[key] ?? null,
          }))
    )

    return [autoOption, ...engineOptions]
  }, [engines])

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Cpu className="w-5 h-5 text-personic-600" />
        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
          Engine Selection
        </h3>
      </div>

      <AnimatePresence mode="wait">
        <div className="space-y-2">
          {options.map((option, index) => {
            const isSelected = value === option.key
            const isAutoTarget = option.key !== 'auto' && option.key === autoEngine

            return (
              <motion.label
                key={option.key}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04, duration: 0.25 }}
                whileHover={disabled ? {} : { scale: 1.005 }}
                whileTap={disabled ? {} : { scale: 0.995 }}
                className={`
                  flex items-start gap-4 p-4 rounded-xl border-2
                  transition-colors duration-150
                  ${isSelected
                    ? 'border-personic-500 bg-personic-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {/* Hidden radio input */}
                <input
                  type="radio"
                  name="pe-engine"
                  value={option.key}
                  checked={isSelected}
                  onChange={() => onChange(option.key)}
                  disabled={disabled}
                  className="sr-only"
                />

                {/* Selection indicator */}
                <div
                  className={`
                    mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center
                    transition-colors duration-150
                    ${isSelected
                      ? 'border-personic-600 bg-personic-600'
                      : 'border-gray-300 bg-white'
                    }
                  `}
                >
                  {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">
                      {option.shortLabel}
                    </span>
                    {option.key !== 'auto' && (
                      <span className="text-xs text-gray-500 truncate">
                        {option.label.replace(`${option.shortLabel} — `, '')}
                      </span>
                    )}
                    {option.key === 'auto' && value === 'auto' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-personic-100 text-personic-700">
                        <Sparkles className="w-3 h-3" />
                        Selects {autoEngine.toUpperCase()} for {productLens || 'default'}
                      </span>
                    )}
                    {isAutoTarget && value === 'auto' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        <Sparkles className="w-3 h-3" />
                        Auto-selected
                      </span>
                    )}
                  </div>

                  <p className="mt-1 text-sm text-gray-500 leading-relaxed">
                    {option.description}
                  </p>

                  {option.threshold !== null && (
                    <p className="mt-1.5 text-xs text-gray-400 font-mono">
                      Default threshold: {option.threshold.toFixed(2)}
                    </p>
                  )}
                </div>
              </motion.label>
            )
          })}
        </div>
      </AnimatePresence>
    </div>
  )
}
